import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { QuestionnaireResponse } from "@medplum/fhirtypes";
import { Loading, useMedplum } from "@medplum/react";

import { Group, Paper, Stack, Title } from "@mantine/core";

import { TextWrapper } from "@/components/TextWrapper";
import { WordsDiff } from "@/components/WordsDiff";
import {
  PostpartumEncounterQuestionnaireURLs,
  PostpartumVitalsQuestionsDisplayNames,
  PostpartumVitalsQuestionsLinkIds,
} from "@/encounters/constants/postpartum-encounter";
import { fetchAmendmentHistoryByQuestionnaireUrl } from "@/encounters/services/questionnaire-response";
import { formatTemperatureDisplay } from "@/encounters/utils/assessments-vitals";
import { getQuestionnaireResponseAnswersSimplified } from "@/utils/medplum";

const VitalsDiff = ({ currentVersionIndex }: { currentVersionIndex: number }) => {
  const { encounterId } = useParams<{ encounterId: string }>();
  const medplum = useMedplum();

  const [isFetchingInitialData, setIsFetchingInitialData] = useState(true);
  const [currentVersion, setCurrentVersion] = useState<QuestionnaireResponse>();
  const [previousVersion, setPreviousVersion] = useState<QuestionnaireResponse>();

  useEffect(() => {
    const getAmendmentHistory = async () => {
      medplum.invalidateSearches("QuestionnaireResponse");
      try {
        const [foundAmendmentHistory] = await fetchAmendmentHistoryByQuestionnaireUrl(
          medplum,
          PostpartumEncounterQuestionnaireURLs.Vitals,
          encounterId!,
        );

        if (!foundAmendmentHistory) return;

        setCurrentVersion(foundAmendmentHistory[currentVersionIndex]);
        setPreviousVersion(foundAmendmentHistory[currentVersionIndex + 1]);
      } finally {
        setIsFetchingInitialData(false);
      }
    };
    getAmendmentHistory();
  }, [currentVersionIndex, encounterId, medplum]);

  if (isFetchingInitialData) return <Loading />;

  // If no vitals data has ever been recorded
  if (!currentVersion) {
    return (
      <Paper p="md" withBorder>
        <Stack mb="lg">
          <Title order={2}>Vitals</Title>
        </Stack>
        <TextWrapper py="lg" c="gray.6" ta="center">
          No vitals data has been recorded for this encounter.
        </TextWrapper>
      </Paper>
    );
  }

  // If vitals data exists but no previous version to compare against,
  // create an empty previous version to reuse the existing diff logic
  const effectivePreviousVersion =
    previousVersion || ({ item: [] } as unknown as QuestionnaireResponse);

  const extractVitalsField = (
    questionnaireResponse: QuestionnaireResponse,
    fieldName: string,
  ): string => {
    const questionnaireResponseAnswers =
      getQuestionnaireResponseAnswersSimplified(questionnaireResponse);

    switch (fieldName) {
      case "heartRate": {
        const heartRateValue =
          questionnaireResponseAnswers[PostpartumVitalsQuestionsLinkIds.PostpartumVitalsHeartRate]
            ?.value[0];
        return heartRateValue != null && heartRateValue !== ""
          ? `${heartRateValue.toString()} bpm`
          : "";
      }
      case "respiratoryRate": {
        const respiratoryRateValue =
          questionnaireResponseAnswers[
            PostpartumVitalsQuestionsLinkIds.PostpartumVitalsRespiratoryRate
          ]?.value[0];
        return respiratoryRateValue != null && respiratoryRateValue !== ""
          ? `${respiratoryRateValue.toString()} bpm`
          : "";
      }
      case "bloodPressure": {
        const systolicBPValue =
          questionnaireResponseAnswers[PostpartumVitalsQuestionsLinkIds.PostpartumVitalsSystolicBP]
            ?.value[0];
        const diastolicBPValue =
          questionnaireResponseAnswers[PostpartumVitalsQuestionsLinkIds.PostpartumVitalsDiastolicBP]
            ?.value[0];
        if (systolicBPValue != null && diastolicBPValue != null) {
          return `${systolicBPValue.toString()}/${diastolicBPValue.toString()} mmHg`;
        }
        if (systolicBPValue != null) {
          return `${systolicBPValue.toString()} mmHg (systolic)`;
        }
        if (diastolicBPValue != null) {
          return `${diastolicBPValue.toString()} mmHg (diastolic)`;
        }
        return "";
      }
      case "temperature": {
        const temperatureValue =
          questionnaireResponseAnswers[
            PostpartumVitalsQuestionsLinkIds.PostpartumVitalsTemperatureValue
          ]?.value[0];
        const temperatureUnitValue =
          questionnaireResponseAnswers[
            PostpartumVitalsQuestionsLinkIds.PostpartumVitalsTemperatureUnit
          ]?.value[0];
        const temperatureMethodValue =
          questionnaireResponseAnswers[
            PostpartumVitalsQuestionsLinkIds.PostpartumVitalsTemperatureMethod
          ]?.value[0];
        return formatTemperatureDisplay(
          temperatureValue,
          temperatureUnitValue,
          temperatureMethodValue,
        );
      }
      case "notes": {
        const notesValue =
          questionnaireResponseAnswers[PostpartumVitalsQuestionsLinkIds.PostpartumVitalsNotes]
            ?.value[0];
        return notesValue?.toString() || "";
      }
      default:
        return "";
    }
  };

  // Check if both versions are completely empty
  const isCompletelyEmpty =
    !extractVitalsField(effectivePreviousVersion, "heartRate") &&
    !extractVitalsField(effectivePreviousVersion, "respiratoryRate") &&
    !extractVitalsField(effectivePreviousVersion, "bloodPressure") &&
    !extractVitalsField(effectivePreviousVersion, "temperature") &&
    !extractVitalsField(effectivePreviousVersion, "notes") &&
    !extractVitalsField(currentVersion, "heartRate") &&
    !extractVitalsField(currentVersion, "respiratoryRate") &&
    !extractVitalsField(currentVersion, "bloodPressure") &&
    !extractVitalsField(currentVersion, "temperature") &&
    !extractVitalsField(currentVersion, "notes");

  if (isCompletelyEmpty) {
    return (
      <Paper p="md" withBorder>
        <Stack mb="lg">
          <Title order={2}>Vitals</Title>
        </Stack>
        <TextWrapper py="lg" c="gray.6" ta="center">
          No vitals data has been recorded for this encounter.
        </TextWrapper>
      </Paper>
    );
  }

  // Extract data for comparison
  const previousHeartRate = extractVitalsField(effectivePreviousVersion, "heartRate");
  const currentHeartRate = extractVitalsField(currentVersion, "heartRate");

  const previousRespiratoryRate = extractVitalsField(effectivePreviousVersion, "respiratoryRate");
  const currentRespiratoryRate = extractVitalsField(currentVersion, "respiratoryRate");

  const previousBloodPressure = extractVitalsField(effectivePreviousVersion, "bloodPressure");
  const currentBloodPressure = extractVitalsField(currentVersion, "bloodPressure");

  const previousTemperature = extractVitalsField(effectivePreviousVersion, "temperature");
  const currentTemperature = extractVitalsField(currentVersion, "temperature");

  const previousNotes = extractVitalsField(effectivePreviousVersion, "notes");
  const currentNotes = extractVitalsField(currentVersion, "notes");

  return (
    <Paper p="md" withBorder>
      <Stack mb="lg">
        <Title order={2}>Vitals</Title>
      </Stack>
      <Group grow align="stretch">
        <Paper p="md" withBorder>
          <Stack>
            <Title mb="lg" order={4} c="gray.6">
              Previous
            </Title>
            <Stack gap="md">
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumVitalsQuestionsDisplayNames.PostpartumVitalsSystolicBP} /{" "}
                  {PostpartumVitalsQuestionsDisplayNames.PostpartumVitalsDiastolicBP}
                </TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentBloodPressure}
                  previous={previousBloodPressure}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumVitalsQuestionsDisplayNames.PostpartumVitalsHeartRate}
                </TextWrapper>
                <WordsDiff showRemoved current={currentHeartRate} previous={previousHeartRate} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumVitalsQuestionsDisplayNames.PostpartumVitalsTemperature}
                </TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentTemperature}
                  previous={previousTemperature}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumVitalsQuestionsDisplayNames.PostpartumVitalsRespiratoryRate}
                </TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentRespiratoryRate}
                  previous={previousRespiratoryRate}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumVitalsQuestionsDisplayNames.PostpartumVitalsNotes}
                </TextWrapper>
                <WordsDiff showRemoved current={currentNotes} previous={previousNotes} />
              </Stack>
            </Stack>
          </Stack>
        </Paper>
        <Paper p="md" withBorder>
          <Stack>
            <Title mb="lg" order={4} c="gray.6">
              Amended
            </Title>
            <Stack gap="md">
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumVitalsQuestionsDisplayNames.PostpartumVitalsSystolicBP} /{" "}
                  {PostpartumVitalsQuestionsDisplayNames.PostpartumVitalsDiastolicBP}
                </TextWrapper>
                <WordsDiff current={currentBloodPressure} previous={previousBloodPressure} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumVitalsQuestionsDisplayNames.PostpartumVitalsHeartRate}
                </TextWrapper>
                <WordsDiff current={currentHeartRate} previous={previousHeartRate} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumVitalsQuestionsDisplayNames.PostpartumVitalsTemperature}
                </TextWrapper>
                <WordsDiff current={currentTemperature} previous={previousTemperature} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumVitalsQuestionsDisplayNames.PostpartumVitalsRespiratoryRate}
                </TextWrapper>
                <WordsDiff current={currentRespiratoryRate} previous={previousRespiratoryRate} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumVitalsQuestionsDisplayNames.PostpartumVitalsNotes}
                </TextWrapper>
                <WordsDiff current={currentNotes} previous={previousNotes} />
              </Stack>
            </Stack>
          </Stack>
        </Paper>
      </Group>
    </Paper>
  );
};

export default VitalsDiff;
